import { InboxNotification, Notification, NotificationKey } from 'models';

import type { NotificationCounts } from '@/collections/notifications/endpoints/counts';

enum NotificationCategory {
    MESSAGES = 'messages',
    ACCESS = 'access',
    COMPANY = 'company',
    CREDITS = 'credits',
    OTHER = 'other',
}

const CATEGORY_BY_TYPE: Record<NotificationKey, NotificationCategory> = {
    [NotificationKey.DIAGRAM_CHAT_MESSAGE]: NotificationCategory.MESSAGES,
    [NotificationKey.INTERCOM_MESSAGE]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_CHAT_REACTION]: NotificationCategory.MESSAGES,
    [NotificationKey.INTERCOM_REACTION]: NotificationCategory.MESSAGES,
    [NotificationKey.ORDER_MESSAGE]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_COMMENT_CREATE]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_COMMENT_REPLY_USER]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_COMMENT_REPLY_AI]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_COMMENT_MENTION_USER]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_COMMENT_MENTION_MANUFACTURER]: NotificationCategory.MESSAGES,
    [NotificationKey.DIAGRAM_INVITE]: NotificationCategory.ACCESS,
    [NotificationKey.USER_TEAM_INVITE]: NotificationCategory.ACCESS,
    [NotificationKey.DIAGRAM_REQUESTACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.COMPANY_REQUEST_ACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.TEAM_REQUEST_ACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.COMPANY_GRANT_ACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.TEAM_GRANT_ACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.COMPANY_DENY_ACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.TEAM_DENY_ACCESS]: NotificationCategory.ACCESS,
    [NotificationKey.DIAGRAM_CHAT_CHANNEL]: NotificationCategory.COMPANY,
    [NotificationKey.COMPANY_ADDED_AS_PARTNER]: NotificationCategory.COMPANY,
    [NotificationKey.COMPANY_NEW_EXHIBITOR_MATCH_LEAD]: NotificationCategory.COMPANY,
    [NotificationKey.COMPANY_NEW_QR_SCAN]: NotificationCategory.COMPANY,
    [NotificationKey.DIAGRAM_CHAT_CREDITS_ESTIMATE]: NotificationCategory.CREDITS,
    [NotificationKey.DIAGRAM_CHAT_CREDITS_ACCEPT]: NotificationCategory.CREDITS,
    [NotificationKey.DIAGRAM_CHAT_CREDITS_DECLINE]: NotificationCategory.CREDITS,
    [NotificationKey.DIAGRAM_CHAT_CREDITS_CHARGE]: NotificationCategory.CREDITS,
    [NotificationKey.ORDER_EVENT]: NotificationCategory.OTHER,
    [NotificationKey.DESIGN_DUPLICATE]: NotificationCategory.OTHER,
};

type Count = { total: number; unread: number };
type CategoryCounts = { all: Count; byCategory: Record<NotificationCategory, Count> };

type InboxQuery = { types?: string[] };
type LocalInboxQuery = InboxQuery & {
    team?: string | null;
    company?: string | null;
    unread?: boolean;
    archived?: boolean;
};

const NotificationHelpers = {
    getQuery: (category: NotificationCategory | null): InboxQuery =>
        category ? { types: Object.values(NotificationKey).filter((type) => CATEGORY_BY_TYPE[type] === category) } : {},

    matchesQuery: (notification: InboxNotification, query: LocalInboxQuery) =>
        (!query.types || query.types.includes(notification.type)) &&
        (!query.unread || !notification.readAt) &&
        Boolean(query.archived) === Boolean(notification.deletedAt) &&
        (!query.team || notification.team === query.team),

    // Mirrors the server's order, rank ascending then newest first, so a notification moved between the inbox and
    // archived views lands where a refetch would put it. A notification that belongs beyond the loaded pages is left
    // out; the next page fetch picks it up. Callers that cannot evaluate every filter locally pass canInsert false,
    // which only removes.
    placeInList: (
        pages: InboxNotification[][],
        notification: InboxNotification,
        query: LocalInboxQuery,
        pageSize: number,
        canInsert = true,
    ): InboxNotification[][] => {
        const remaining = pages.map((page) =>
            page.filter((pageNotification) => pageNotification.id !== notification.id),
        );

        if (!canInsert || !NotificationHelpers.matchesQuery(notification, query)) {
            return remaining;
        }

        const sortsAfter = (pageNotification: InboxNotification) =>
            pageNotification.rank > notification.rank ||
            (pageNotification.rank === notification.rank && pageNotification.createdAt < notification.createdAt);

        const pageIndex = remaining.findIndex((page) => page.some(sortsAfter));

        if (pageIndex !== -1) {
            return remaining.map((page, index) => {
                if (index !== pageIndex) {
                    return page;
                }

                const position = page.findIndex(sortsAfter);

                return [...page.slice(0, position), notification, ...page.slice(position)];
            });
        }

        const hasMorePages = (pages.at(-1)?.length ?? 0) >= pageSize;

        if (hasMorePages) {
            return remaining;
        }

        return remaining.length
            ? [...remaining.slice(0, -1), [...remaining[remaining.length - 1], notification]]
            : [[notification]];
    },

    isMessage: (notification: Notification) => CATEGORY_BY_TYPE[notification.type] === NotificationCategory.MESSAGES,

    getCategoryCounts: (countsByType: NotificationCounts['byType']): CategoryCounts => {
        const categoryCounts: CategoryCounts = {
            all: { total: 0, unread: 0 },
            byCategory: Object.fromEntries(
                Object.values(NotificationCategory).map((category) => [category, { total: 0, unread: 0 }]),
            ) as CategoryCounts['byCategory'],
        };

        for (const [type, count] of Object.entries(countsByType)) {
            const category = CATEGORY_BY_TYPE[type as NotificationKey];

            categoryCounts.all = addCounts(categoryCounts.all, count);

            if (category) {
                categoryCounts.byCategory[category] = addCounts(categoryCounts.byCategory[category], count);
            }
        }

        return categoryCounts;
    },

    getSender: (notification: Notification): { user?: string; company?: string } | null => {
        switch (notification.type) {
            case NotificationKey.COMPANY_NEW_EXHIBITOR_MATCH_LEAD:
            case NotificationKey.COMPANY_GRANT_ACCESS:
            case NotificationKey.COMPANY_DENY_ACCESS:
                return { company: notification.data.companyId };
            case NotificationKey.COMPANY_ADDED_AS_PARTNER:
                return { company: notification.data.addedByCompanyId };
        }

        if (notification.fromCompany) {
            return { company: notification.fromCompany };
        }

        return notification.from ? { user: notification.from } : null;
    },
};

const addCounts = (first: Count, second: Count): Count => ({
    total: first.total + second.total,
    unread: first.unread + second.unread,
});

export type { Count, InboxQuery, LocalInboxQuery };
export { NotificationCategory, NotificationHelpers };
